import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { 
  Box, Heading, Text, Image, Flex, 
  Skeleton, Stack, Divider, Badge
} from "@chakra-ui/react";
import { supabase } from "../integrations/supabase/client";

const ArticlePage = () => {
  const { articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchArticle = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Find article using article_id
        const { data, error } = await supabase
          .from('article')
          .select('*')
          .eq('article_id', articleId)
          .single();
          
        if (error) throw error;
        
        if (data) {
          // Get feed information
          const { data: feedData } = await supabase
            .from('feed')
            .select('*')
            .eq('feed_id', data.feed_id)
            .single();

          setArticle({
            ...data,
            feed: feedData || null
          });
        } else {
          setError("Article not found");
        }
      } catch (error) {
        console.error('Error fetching article:', error);
        setError(error.message || "Error loading article");
      } finally {
        setIsLoading(false);
      }
    };
    
    if (articleId) {
      fetchArticle();
    }
  }, [articleId]);
  
  if (isLoading) {
    return (
      <Box maxWidth="900px" mx="auto" py={8} px={4}>
        <Stack spacing={4}>
          <Skeleton height="60px" />
          <Skeleton height="20px" width="30%" />
          <Skeleton height="400px" />
          <Skeleton height="20px" />
          <Skeleton height="20px" />
          <Skeleton height="20px" width="70%" />
        </Stack>
      </Box>
    );
  }
  
  if (error || !article) {
    return (
      <Box maxWidth="900px" mx="auto" py={8} px={4} textAlign="center">
        <Heading as="h1" mb={4} color="red.500">
          Article Loading Failed
        </Heading>
        <Text>{error || "Article not found"}</Text>
      </Box>
    );
  }
  
  const getCategoryColor = (category) => {
    switch(category) {
      case 'AI行业动态': return 'blue';
      case 'Web3动态': return 'orange';
      case '企业家动态': return 'green';
      case '投资人动态': return 'purple';
      case '一人公司动态': return 'teal';
      case '科技': return 'green';
      default: return 'gray';
    }
  };
  
  return (
    <Box maxWidth="900px" mx="auto" py={8} px={4}>
      <Heading as="h1" size="xl" mb={4}>
        {article.title}
      </Heading>
      
      <Flex 
        justify="space-between" 
        align="center" 
        mb={6} 
        flexWrap="wrap"
        gap={2}
      >
        <Flex align="center" gap={2}>
          {article.feed?.category && (
            <Badge 
              colorScheme={getCategoryColor(article.feed.category)}
              variant="solid"
              fontSize="sm"
            >
              {article.feed.category}
            </Badge>
          )}
          {article.author && (
            <Text fontSize="sm" color="gray.600">
              {article.author}
            </Text>
          )}
        </Flex>
        
        <Text fontSize="sm" color="gray.600">
          {new Date(article.published_at).toLocaleString()}
        </Text>
      </Flex>
      
      {article.media_url && (
        <Box mb={6}>
          <Image
            src={article.media_url}
            alt={article.title}
            width="100%"
            maxHeight="400px"
            objectFit="cover"
            borderRadius="md"
          />
        </Box>
      )}
      
      <Divider mb={6} />
      
      <Text fontSize="lg" whiteSpace="pre-line">
        {article.description}
      </Text>
      
      {article.url && (
        <>
          <Divider my={6} />
          <Text mb={2} fontWeight="bold">
            Original Link:
          </Text>
          <Box 
            as="a" 
            href={article.url} 
            target="_blank"
            rel="noopener noreferrer"
            color="blue.500"
            wordBreak="break-all"
          >
            {article.url}
          </Box>
        </>
      )}
      
      {article.feed && (
        <>
          <Divider my={6} />
          <Text mb={2} fontWeight="bold">
            Source:
          </Text>
          <Flex align="center">
            {article.feed.image && (
              <Image
                src={article.feed.image}
                alt={article.feed.title}
                boxSize="30px"
                borderRadius="full"
                mr={2}
                objectFit="cover"
              />
            )}
            <Text>{article.feed.title}</Text>
          </Flex>
          {article.feed.description && (
            <Text mt={2} fontSize="sm" color="gray.600">
              {article.feed.description}
            </Text>
          )}
        </>
      )}
    </Box>
  );
};

export default ArticlePage; 